import { Alert, DatePickerPopover, flash, formatDate, Modal, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, handleSuccessfulSubmission, now, parseTime } from "common";
import dayjs, { Dayjs } from "dayjs";
import { isNull } from "lodash";
import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Except } from "type-fest";
import {
    AllowedPrivacy,
    Arrival,
    Event,
    Experience,
    OPEN_UNLIMITED,
    Permission,
    PurchaseStatus,
    Timeslot,
} from "types";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useCollection } from "../../hooks/useCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { getExperienceMax } from "../../models/Experience";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { QuantitySpinnerSkeleton } from "../Common/QuantitySpinner/QuantitySpinnerSkeleton";
import { SecondaryButton } from "../Common/SecondaryButton";
import { invalidateCacheForIndividualTimeslotRequests } from "../PurchaseCreate/hooks/useArrivalTimeslots";
import { TimeslotTimePicker } from "./TimeslotTimePicker";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface CreateExperienceTimeslotModalProps {
    isOpen: boolean;
    timeslot: Timeslot;
    experienceId: ID;
    initialValue?: Dayjs | null;
    showCapacity: boolean;
    showAllTimes: boolean;
    disabledDays: (date: Date) => void;
    onClose: (timeslot: Timeslot, date?: Dayjs, shouldRefetch?: boolean) => void;
}

export const CreateExperienceTimeslotModal = ({
    isOpen,
    timeslot,
    experienceId,
    initialValue,
    showCapacity,
    showAllTimes,
    disabledDays,
    onClose,
}: CreateExperienceTimeslotModalProps) => {
    return (
        <Modal size="large" isOpen={isOpen} onClose={onClose}>
            <ModalContent
                timeslot={timeslot}
                experienceId={experienceId}
                initialValue={initialValue}
                showCapacity={showCapacity}
                showAllTimes={showAllTimes}
                disabledDays={disabledDays}
                onClose={onClose}
            />
        </Modal>
    );
};

const ModalContent = ({
    onClose,
    initialValue,
    timeslot: initialTimeslot,
    showCapacity,
    showAllTimes,
    disabledDays,
    experienceId,
}: Except<CreateExperienceTimeslotModalProps, "isOpen">) => {
    const timeslotFromStore = useCreatePurchaseStore((state) => state.getActiveItem()?.timeslot);
    const timeslot = timeslotFromStore ?? initialTimeslot;
    const invalidate = useInvalidate();
    const seller = useSeller();
    const { isGranted } = useAuthorization();
    const { data: experience } = useResource<Experience>(["products", experienceId]);
    const [capacity, setCapacity] = useState(0);
    const [value, setValue] = useState<Arrival>({ date: initialValue ? formatDate(initialValue) : "", time: null });
    const date = getDate(value);

    const experienceCapacityPath = [
        "experiences",
        experienceId,
        "capacity",
        dayjs(getDate(value)).format("YYYY-MM-DDTHH:mm:00"),
    ];
    const { isLoading: isLoadingCapacity, isFetching: isFetchingCapacity } = useQuery(
        [...experienceCapacityPath, value, experience],
        async () => {
            const { data } = await api.get<Event>(`/${experienceCapacityPath.join("/")}`);
            setCapacity(data.open || getExperienceMax(experience));
            return data;
        },
        { staleTime: 100, enabled: !!value.date },
    );

    const isLoadingExperienceCapacity = isLoadingCapacity || isFetchingCapacity;

    const {
        mutate,
        isLoading: isUpdating,
        isSuccess,
    } = useMutation(async () => {
        const { data: newEvent } = await api.post<Event>(`/events?seller=${seller.id}`, {
            experience: { id: experienceId },
            start: date.utc(true).format(), // UTC offset is required because of quirkiness of event's start attribute
            max: capacity === OPEN_UNLIMITED ? null : capacity,
            open: capacity,
            manual: true,
            private: false,
            notes: [],
            virtualMeeting: false,
        });

        const eventDuration = experience?.eventDuration ?? experience?.duration ?? 0;

        const artificialTimeslot: any = {
            arrivalDatetime: now(date.utc(true).format()),
            canBook: newEvent.open > 0,
            capacity: { open: newEvent.open },
            date: formatDate(date),
            end: date.add(eventDuration, "minutes").subtract(1, "seconds"),
            id: `${experienceId}_${formatDate(date)}_${formatDate(date, DateFormat.TIME_SHORT)}_${newEvent.id}`,
            privacy: timeslot?.privacy ?? AllowedPrivacy.PUBLIC,
            product: experienceId,
            scheduleType: experience?.scheduleType,
            time: formatDate(date, DateFormat.TIME_SHORT),
            units: timeslot?.units,
        };

        if (newEvent.open <= 0) {
            flash.show({
                text: "The timeslot has been created. However, it cannot be used to make reservations due to insufficient availability",
                color: "danger",
            });
        }

        handleSuccessfulSubmission(() => {
            // Whenever new timeslot is created, We add the newly created to selectedTimeSlots(to display) if there are open seats.
            // But we should also add them, if ShowAllTimes toggle is enabled
            const state = useCreatePurchaseStore.getState();

            if (state.purchase.status === PurchaseStatus.DRAFT) {
                const privacy = state.getActiveItem()?.privacy;
                invalidateCacheForIndividualTimeslotRequests(experienceId, privacy);
            }

            onClose(artificialTimeslot, newEvent.open > 0 || showAllTimes ? date : undefined, true);
            invalidate("events");
        });
    });

    const { data: timeslots, isLoading: isLoadingTimeslots } = useCollection<Timeslot>("timeslots", {
        start: value.date,
        product: experienceId,
    });

    const handleDayChange = (date: Date, options: { disabled: boolean }) => {
        if (!options?.disabled) {
            setValue({ date: formatDate(date), time: null });
        }
    };

    const handleTimeChange = (time: number) => {
        setValue({ ...value, time });
    };

    const timeslotAlreadyExists = timeslots.some((timeslot) => {
        return timeslot.date === value.date && timeslot.time === value.time;
    });
    const isArrivalIncomplete = !value.date || isNull(value.time);

    return (
        <>
            <Modal.Header>Add New Timeslot</Modal.Header>

            <Modal.Body>
                <div className={clsx("flex", showCapacity ? "justify-between" : null)}>
                    <div className="space-y-1">
                        <span className="text-sm font-bold">Date</span>

                        <div>
                            <DatePickerPopover
                                shouldShowYearPicker
                                value={value.date ? date.toDate() : undefined}
                                isLoading={isUpdating}
                                disabledDays={disabledDays}
                                popoverProps={{ appendTo: document.body, zIndex: 40, placement: "auto" }}
                                onChange={handleDayChange}
                            />
                        </div>

                        <TimeslotTimePicker value={value.time} isLoading={isUpdating} onChange={handleTimeChange} />
                    </div>

                    {capacity !== OPEN_UNLIMITED && showCapacity && isGranted(Permission.MODIFY_CAPACITY) ? (
                        isLoadingExperienceCapacity ? (
                            <QuantitySpinnerSkeleton text="Capacity" />
                        ) : (
                            <QuantitySpinner
                                size="large"
                                isLoading={isUpdating}
                                value={capacity}
                                text="Capacity"
                                onChange={(value: number) => setCapacity(value)}
                            />
                        )
                    ) : null}
                </div>

                {timeslotAlreadyExists ? (
                    <Alert className="mt-4 text-center" color="danger">
                        Can&apos;t open a manual timeslot on a valid booking date
                    </Alert>
                ) : null}
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                <SubmitButton
                    isLoading={isUpdating || isLoadingTimeslots}
                    isSuccess={isSuccess}
                    disabled={isArrivalIncomplete || timeslotAlreadyExists}
                    onClick={mutate}
                >
                    Create
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

const getDate = (arrival: Arrival) => {
    const date = dayjs(arrival.date);

    if (arrival.time !== null) {
        const { hours, minutes } = parseTime(arrival.time);
        return date.hour(hours).minute(minutes).startOf("minute");
    }

    return date;
};
